import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Button, Col, Form, Row } from 'react-bootstrap';
import { useMergedState } from '../../hooks/useMergedState';
import { IBaseForm, IBaseState } from '../../types/types';
import validate from 'validate.js';
import { profilePageConstraint } from '../../constraints/ProfilePage';
import Alert, { AlertType } from '../components/Alert';
import RequestHelper from '../../apis/RequestHelper';
import RegisterContext from '../register/RegisterContext';
import { IUserData } from '../../types/user';
import CasCalendar from '../components/CasCalendar';
import { RangeKeyDict } from 'react-date-range';
import { UseTranslate } from '../../hooks/Translate';
import UserContactInformation from './components/UserContactInformation';
import { AxiosError } from 'axios';
import { AuthContext } from 'orinabiji-auth-react';

interface IProfilePageState extends IBaseForm {
  valueChanged: boolean;
}

type ProfilePageState = Partial<IBaseState & IProfilePageState>;

const ProfilePage = () => {
  const [dateSelect, setDateSelect] = useState<Date>(new Date());
  const [state, setState] = useMergedState<ProfilePageState>({
    errors: {},
    validated: false,
    alertProps: {
      type: AlertType.NONE,
    },
    valueChanged: false,
  });

  const [translate] = UseTranslate();
  const { setAlertProps } = useContext(RegisterContext);
  const { userInfo } = useContext(AuthContext);
  const [userData, setUserData] = useState<IUserData>({});
  const getUserInfoDetails = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/details`;
      const res = await RequestHelper.cas.get(url);
      if (res.data.status === 'SUCCESS') {
        setUserData(res.data.data);
        if (res.data.data.birthDate) {
          setDateSelect(new Date(res.data.data.birthDate));
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    getUserInfoDetails();
  }, [getUserInfoDetails]);

  const onFormSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());
    const result = validate(
      values,
      profilePageConstraint(translate('cas.profile.fieldRequired')),
      { fullMessages: false }
    );

    if (result) {
      setState({ errors: result });
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.alert.errorTitle'),
        message: 'cas.alert.errorMessage',
      });
    } else {
      setState({ errors: {} });

      try {
        const url = `${process.env.REACT_APP_CAS_API}/users/${userInfo.userId}`;
        const res = await RequestHelper.cas.put(url, {
          firstName: userData.firstName,
          lastName: userData.lastName,
          gender: userData.gender,
          birthDate: dateSelect,
        });

        if (res.data.status === 'FAIL') {
          setState({
            alertProps: {
              type: AlertType.ERROR,
              title: translate('cas.alert.errorTitle'),
              message: translate(res.data.data.message),
            },
          });
        } else {
          setState({
            valueChanged: false,
            alertProps: {
              type: AlertType.SUCCESS,
              title: translate('cas.alert.successTitle'),
              message: translate('cas.alert.successMessage'),
            },
          });
        }
      } catch (e) {
        let message;
        if (e instanceof AxiosError && e.response) {
          message = translate(e?.response?.data?.message);
        }
        setState({
          alertProps: {
            type: AlertType.ERROR,
            title: translate('cas.alert.errorTitle'),
            message,
          },
        });
      }
    }

    setState({ validated: true });
  };

  const controlStyle = {
    height: 51,
    background: '#F4F5F7',
    border: '1px solid #E6E7E8',
    borderRadius: 6,
  };

  const alertPadding = { paddingLeft: 0, paddingRight: 5 };

  const onDateConfirm = (value: RangeKeyDict | Date) => {
    //Calendar trigers oncorfirm which sets valueChanged: true
    //Do that only when date is changing
    if (
      value &&
      dateSelect &&
      value instanceof Date === true &&
      dateSelect !== value
    ) {
      setDateSelect(value as Date);
      setState({ valueChanged: true });
    }
  };

  const onGenericChange = (e: any) => {
    userData[e.target.name] = e.target.value;
    setState({ valueChanged: true });
    setUserData(userData);
  };

  return (
    <>
      <div className="cas-content-wrapper">
        <div className="cas-content">
          <div className="cas-profile-content">
            <div className="container-title">
              {translate('cas.profile.pageTitle')}
            </div>

            <div className="col-md-3" style={alertPadding}>
              <Alert
                type={state.alertProps!.type}
                title={state.alertProps!.title}
                message={state.alertProps!.message}
              />
            </div>

            <Form
              validated={state.validated}
              noValidate
              onSubmit={onFormSubmit}
            >
              <Row>
                <Col md={3} className="margin-r-40">
                  <Form.Group className="mb-3" controlId="firstName">
                    <Form.Label className="small user-icon">
                      {' '}
                      {translate('cas.profile.firstName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={userData.firstName || ''}
                      name="firstName"
                      placeholder={translate('cas.profile.enterFirsName')}
                      isInvalid={!!state.errors?.firstName}
                      onChange={onGenericChange}
                      required
                      style={controlStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors?.firstName && state.errors?.firstName[0]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3" controlId="lastName">
                    <Form.Label className="small user-icon">
                      {' '}
                      {translate('cas.profile.lastName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={userData.lastName || ''}
                      placeholder={translate('cas.profile.enterLastName')}
                      isInvalid={!!state.errors?.lastName}
                      onChange={onGenericChange}
                      required
                      style={controlStyle}
                    />
                    <Form.Control.Feedback type="invalid">
                      {state.errors?.lastName && state.errors?.lastName[0]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={3} className="margin-r-40">
                  <Form.Group className="mb-3">
                    <Form.Label className="small email-icon">
                      {' '}
                      {translate('cas.profile.birthDate')}
                    </Form.Label>
                    <CasCalendar
                      type="Calendar"
                      onConfirm={onDateConfirm}
                      onLocaleChange={onDateConfirm}
                      selectionRange={dateSelect}
                      showIcon={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="gender">
                    <Form.Label className="small email-icon">
                      {' '}
                      {translate('cas.profile.gender')}
                    </Form.Label>
                    <Form.Select
                      name="gender"
                      className="custom-dropdown"
                      onChange={(e) => {
                        userData.gender = Number.parseInt(e.target.value);
                        setUserData(userData);
                        setState({ valueChanged: true });
                      }}
                      defaultValue={userData.gender}
                      value={userData.gender}
                    >
                      <option value="1" >
                        {' '}
                        {translate('cas.profile.male')}
                      </option>
                      <option value="2" >
                        {' '}
                        {translate('cas.profile.female')}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Button
                type="submit"
                className="mt-3 custom-submit-button"
                disabled={!state.valueChanged}
              >
                {translate('cas.profile.save')}
              </Button>
            </Form>
          </div>
        </div>

        <div className="cas-content">
          <UserContactInformation
            email={userData.email}
            phoneNumber={userData.phoneNumber}
            userId={userData._id}
            reloadUserDetail={getUserInfoDetails}
          ></UserContactInformation>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
